import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Button, CircularProgress } from '@mui/joy';
import { motion } from 'framer-motion';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { Logo } from './Logo';
import { clearToken, getToken } from './adminApi';

const navItems = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: <DashboardIcon /> },
  { label: 'New Post', path: '/admin/blog/new', icon: <AddIcon /> },
];

export const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    fetch('/api/admin/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Unauthorized');
        return res.json();
      })
      .then((data) => {
        setEmail(data.email);
        setIsAuthenticated(true);
      })
      .catch(() => {
        clearToken();
        setIsAuthenticated(false);
      });
  }, []);

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    clearToken();
    toast.info('Logged out');
    navigate('/admin', { replace: true });
  };

  if (isAuthenticated === null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box
        component="header"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2, md: 4 },
          py: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(12px)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Link to="/admin/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Logo size={32} />
            <Typography level="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
              CTS Admin
            </Typography>
          </Link>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path === '/admin/dashboard' && location.pathname.startsWith('/admin/blog') && location.pathname.includes('/edit'));
            return (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                variant={isActive ? 'solid' : 'plain'}
                size="sm"
                startDecorator={item.icon}
                sx={isActive ? {} : { color: 'text.secondary' }}
              >
                {item.label}
              </Button>
            );
          })}
          <Button
            component={Link}
            to="/"
            target="_blank"
            variant="plain"
            size="sm"
            startDecorator={<VisibilityIcon />}
            sx={{ color: 'text.secondary' }}
          >
            View Site
          </Button>
          <Typography level="body2" sx={{ color: 'text.secondary', ml: 2 }}>
            {email}
          </Typography>
          <Button
            variant="plain"
            size="sm"
            onClick={handleLogout}
            startDecorator={<LogoutIcon />}
            sx={{ color: 'text.secondary' }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ flex: 1 }}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Outlet />
        </Container>
      </motion.div>
    </Box>
  );
};

export default AdminLayout;
